<script setup lang="ts">
import { computed, nextTick, onMounted, PropType, ref, watch } from 'vue'
import moment from 'moment'

const props = defineProps({
  value: {
    type: null as unknown as PropType<[Date, Date] | null>,
    default: () => null,
  },
  label: { type: String, default: '' },
  options: { type: null, default: undefined },
  maxRange: { type: Number as PropType<number | null>, default: null },
})

const emit = defineEmits<{
  (e: 'input', value: [Date, Date] | null): void
}>()

const dateFormat = 'YYYY-MM-DD'
const dateRangeModel = ref<string[] | null>(null)

const formatDate = (date: Date | null): string | null => {
  return date ? moment(date).format(dateFormat) : null
}

const selectedRangeLabel = computed(() => {
  const model = dateRangeModel.value
  if (model && model.length === 2) {
    // Two dates selected
    const start = moment(model[0], dateFormat)
    const end = moment(model[1], dateFormat)
    // Ensure diff is always positive
    const duration = Math.abs(end.diff(start, 'days')) + 1
    const displayDuration = Math.max(duration, 1)
    const suffix = displayDuration > 1 ? 's' : ''
    return `${displayDuration} day${suffix} selected`
  }
  // Only other case is null or invalid model -> show default
  return 'Select date range'
})

const clearRange = () => {
  emit('input', null)
  dateRangeModel.value = null
}

const resetModelFromProps = () => {
  // Expect value to be null or [Date, Date]
  if (!props.value || !Array.isArray(props.value)) {
    dateRangeModel.value = null
    return
  }

  // Derive start/end date from props.value for internal model
  const [startDate, endDate] = props.value
  const startStr = formatDate(startDate)
  const endStr = formatDate(endDate)

  if (startStr && endStr) {
    dateRangeModel.value = moment(startStr).isAfter(endStr)
      ? [endStr, startStr]
      : [startStr, endStr]
  } else {
    // If dates in array are invalid, reset model
    dateRangeModel.value = null
  }
}

const hasDateRangeLimit = computed(
  () => dateRangeModel.value?.length === 1 && props.maxRange !== null,
)

const minAllowedDate = computed(() => {
  if (!hasDateRangeLimit.value || !dateRangeModel.value) return undefined
  return moment(dateRangeModel.value[0]).subtract(props.maxRange, 'day').format(dateFormat)
})

const maxAllowedDate = computed(() => {
  if (!hasDateRangeLimit.value || !dateRangeModel.value) return undefined
  return moment(dateRangeModel.value[0]).add(props.maxRange, 'day').format(dateFormat)
})

watch(
  () => props.value,
  () => {
    resetModelFromProps()
  },
  { immediate: true },
)

const onDatePickerChange = (newRangeStrings: string[]) => {
  if (newRangeStrings && newRangeStrings.length === 2) {
    // Complete range selected
    const [firstDate, secondDate] = newRangeStrings.map((d) => moment(d))
    const [startMoment, endMoment] = firstDate.isBefore(secondDate)
      ? [firstDate, secondDate]
      : [secondDate, firstDate]

    dateRangeModel.value = [startMoment.format(dateFormat), endMoment.format(dateFormat)]

    // Emit the selected range as a [Date, Date] array
    emit('input', [startMoment.startOf('day').toDate(), endMoment.endOf('day').toDate()])
  } else {
    // Range is incomplete (0 or 1 date) or cleared
    dateRangeModel.value = newRangeStrings?.length === 1 ? newRangeStrings : null
    // Emit null as the value
    emit('input', null)
  }
}

const rootElement = ref(null)

onMounted(() => {
  nextTick(() => {
    if (rootElement.value) {
      const el = rootElement.value as HTMLElement
      el.focus()
    }
  })
})
</script>

<template>
  <div ref="rootElement" class="date-range-wrapper">
    <v-date-picker
      v-model="dateRangeModel"
      tabindex="0"
      class="date-picker"
      no-title
      range
      :min="minAllowedDate"
      :max="maxAllowedDate"
      show-adjacent-months
      @change="onDatePickerChange"
    />
    <div class="selected-range-display mt-2">
      <vs-text type="subdued" class="range-label">{{ selectedRangeLabel }} </vs-text>
      <v-btn
        v-if="props.value"
        icon
        x-small
        class="ml-1 clear-button"
        title="Clear date range"
        @click="clearRange"
      >
        <v-icon small color="red">mdi-close</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<style scoped>
/* Styles copied from FilterAsideDateRange.vue - may need adjustments */
.date-picker {
  padding: 0;
  border: none;
  border-radius: 0;
}

.selected-range-display {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  margin-top: 4px;
}

.range-label {
  text-align: center;
}

.clear-button {
  position: relative;
  top: -1px;
}

:deep(.v-btn--disabled.accent) {
  background-color: var(--color-blue--lighter) !important;
  color: var(--color-blue--light) !important;
}

:deep(.v-date-picker-table--date .v-btn) {
  width: 24px;
  height: 24px;
}

:deep(.v-picker__body) {
  width: 100% !important;
}

:deep(.v-date-picker-table) {
  height: auto;
}

:deep(.v-date-picker-header__value) {
  font-family: var(--font-display);
}

.date-range-wrapper {
  max-width: 320px;
  margin: 0 auto;
  outline: none;
}
</style>
